import { useState, useReducer } from "react";
import { Button } from "react-bootstrap";
import { useSelector } from "react-redux";
import Modal from "react-bootstrap/Modal";

import useGetLetterTemplates from "../hooks/useGetLetterTemplates";
import TableFirmSettings from "../../common/table-firm-settings";
import { formatDate, getLoggedInUserId, getCaseId, getClientId } from "../../../../Utils/helper";
import api from "../../../../api/api";
import EditCopyLetterTemplateModal from "../modals/EditLetterTemplateModal";
import UserChatBubble from "../../../Forms/FirmUserBtn";
import ChatSendingPopup from "../../../Forms/common/ChatSendingPopup";
import avatarImage from "../../../../assets/images/avatar.svg";
import "./LetterTemplateTabs.css";

const LetterTemplatesALL = () => {
  const { data: getLetterTemplates, refetch } = useGetLetterTemplates();
  const origin = process.env.REACT_APP_BACKEND_URL;
  const userOnlineStatus = useSelector((state) => state.chat2.userOnlineStatus);

  const [chatData, setChatData] = useState(null);
  const [chatModal, setChatModal] = useState(false);
  const openChatModel = (event, data) => {
    event?.stopPropagation();
    setChatData(data);
    setChatModal(!chatModal);
  };

  const initialState = {
    show: false,
    status: "",
  };

  function reducer(state, action) {
    switch (action.type) {
      case "SHOW_MODAL":
        return { ...state, show: true, status: action.payload };
      case "HIDE_MODAL":
        return { ...state, show: false };

      default:
        return state;
    }
  }

  const [state, dispatch] = useReducer(reducer, initialState);

  const lockedMessage = (editBy, profilePic) => `
    <p>
      The template is being edited by
      <span className="d-flex align-items-center justify-content-center">
        <span className="ic ic-avatar ic-29 has-avatar-icon has-cover-img m-r-5">
          ${profilePic ? `<img src="${profilePic}" className="theme-ring">` : ""}
        </span>
        <span>${editBy?.first_name || "Another user"} ${editBy?.last_name || ""}</span>
      </span>
      It will be available when that user closes it or the editing session expires.
    </p>`;

  const createEditDoc = async (docId, name, dynamicTemplateId) => {
    try {
      const wpUrl =
        "/bp-wordprocessor/" +
        getClientId() +
        "/" +
        getCaseId() +
        `/?docId=${docId}&dynamic_template_id=${dynamicTemplateId}&type=LetterTemplate`;
      const formData = new FormData();
      formData.append("doc_id", docId);
      formData.append("url", wpUrl);
      formData.append("name", name);
      formData.append("template_id", dynamicTemplateId);
      formData.append("edit_mode", "dlt_template");

      const response = await api.post(`${origin}/api/create-edit-doc/`, formData);
      const session = response.data;
      const sessionUrl =
        "/bp-wordprocessor/" +
        getClientId() +
        "/" +
        getCaseId() +
        `/?docId=${session.doc_id}&updatedDocId=${session.updated_doc_id}` +
        `&dynamic_template_id=${session.template_id}&editDocId=${session.id}` +
        `&expiresAt=${encodeURIComponent(session.expires_at)}` +
        "&type=LetterTemplate";
      window.location.href = sessionUrl;
    } catch (error) {
      const data = error?.response?.data;
      if (error?.response?.status === 423) {
        dispatch({
          type: "SHOW_MODAL",
          payload: lockedMessage(data?.locked_by_user),
        });
      } else {
        dispatch({
          type: "SHOW_MODAL",
          payload: data?.error || "The template could not be opened.",
        });
      }
      await refetch();
    }
  };

  const userId = getLoggedInUserId();
  const handleEditButton = async (
    docId,
    loggedInUserId,
    templateId,
    templateName,
    _edit,
    _edit_by,
    _edit_by_profile_pic
  ) => {
    const freshTemplates = await refetch();
    const freshTemplate = freshTemplates?.find((item) => item.id === templateId);
    if (freshTemplate?.edit) {
      dispatch({
        type: "SHOW_MODAL",
        payload: lockedMessage(
          freshTemplate.lock_owner || freshTemplate.edit_by,
          freshTemplate.edit_by_profile_pic
        ),
      });
    } else {
      await createEditDoc(docId, templateName, templateId);
    }
  };
  const [editData, setEditData] = useState();
  const [showEditModal, setShowEditModal] = useState(false);
  const handleEditModalShow = async (
    docId,
    loggedInUserId,
    templateId,
    _templateName,
    _edit,
    _edit_by,
    _edit_by_profile_pic
  ) => {
    const freshTemplates = await refetch();
    const freshTemplate = freshTemplates?.find((item) => item.id === templateId);
    if (freshTemplate?.edit) {
      dispatch({
        type: "SHOW_MODAL",
        payload: lockedMessage(
          freshTemplate.lock_owner || freshTemplate.edit_by,
          freshTemplate.edit_by_profile_pic
        ),
      });
    } else {
      try {
        const response = await api.get("/api/firmsetting-page/edit-dl-template/", {
          params: {
            template_id: templateId,
          },
        });
        setEditData(response.data);
        setShowEditModal(true);
      } catch (error) {
        console.error(error);
      }
    }
  };

  return (
    <>
      <TableFirmSettings>
        <thead>
          <tr>
            <th
              className="td-autosize"
              style={{
                minWidth: "25px",
                width: "25px",
                maxWidth: "25px",
              }}
            />

            <th
              className="td-autosize text-center"
              style={{ fontSize: "14px", fontWeight: "bold" }}
            >
              Template Name
            </th>
            <th
              style={{
                minWidth: "15px",
                width: "15px",
                maxWidth: "15px",
              }}
            />
            <th
              className="td-autosize text-center"
              style={{ fontSize: "14px", fontWeight: "bold" }}
            >
              File Name
            </th>
            <th />
            <th
              className="td-autosize text-center"
              style={{ fontSize: "14px", fontWeight: "bold" }}
            >
              Page
            </th>
            <th
              style={{
                minWidth: "15px",
                width: "15px",
                maxWidth: "15px",
              }}
            />
            <th
              className="td-autosize text-center"
              style={{ fontSize: "14px", fontWeight: "bold" }}
            >
              Generate Document Popup
            </th>
            <th />
            <th
              colSpan={2}
              className="td-autosize text-center"
              style={{ fontSize: "14px", fontWeight: "bold" }}
            >
              Last Edited
            </th>
            <th
              style={{
                minWidth: "25px",
                width: "25px",
                maxWidth: "25px",
              }}
            />
            {/* <th style={{ fontSize: "14px", fontWeight: "bold" }}></th> */}
            <th className="td-autosize text-center" />
            <th
              style={{
                minWidth: "15px",
                width: "15px",
                maxWidth: "15px",
              }}
            />
          </tr>
        </thead>
        <tbody>
          {getLetterTemplates &&
            getLetterTemplates?.map((letter, idx) => {
              return (
                <tr style={{ height: "25px" }}>
                  <td
                    style={{ fontSize: "13px" }}
                    onClick={() =>
                      handleEditModalShow(
                        letter?.for_template?.template?.id,
                        userId,
                        letter?.id,
                        letter?.for_template.template_name,
                        letter?.edit,
                        letter?.edit_by,
                        letter?.edit_by_profile_pic
                      )
                    }
                  >
                    {idx + 1}
                  </td>

                  <td
                    className="text-start"
                    style={{ fontSize: "13px" }}
                    onClick={() =>
                      handleEditModalShow(
                        letter?.for_template?.template?.id,
                        userId,
                        letter?.id,
                        letter?.for_template.template_name,
                        letter?.edit,
                        letter?.edit_by,
                        letter?.edit_by_profile_pic
                      )
                    }
                  >
                    {letter?.for_template?.template_name}
                  </td>
                  <td />
                  <td
                    className="text-start"
                    style={{ fontSize: "13px" }}
                    onClick={() =>
                      handleEditModalShow(
                        letter?.for_template?.template?.id,
                        userId,
                        letter?.id,
                        letter?.for_template.template_name,
                        letter?.edit,
                        letter?.edit_by,
                        letter?.edit_by_profile_pic
                      )
                    }
                  >
                    {letter?.for_template?.template?.file_name ?? ""}
                  </td>
                  <td />
                  <td
                    style={{ fontSize: "13px" }}
                    onClick={() =>
                      handleEditModalShow(
                        letter?.for_template?.template?.id,
                        userId,
                        letter?.id,
                        letter?.for_template.template_name,
                        letter?.edit,
                        letter?.edit_by,
                        letter?.edit_by_profile_pic
                      )
                    }
                  >
                    <span className="d-flex align-items-center">
                      <span className="ic ic-15 d-flex align-items-center justify-content-center">
                        <img
                          src={letter?.for_page?.page_icon}
                          alt={`${letter?.for_page?.page_icon} Icon`}
                        />
                      </span>
                      <span
                        style={{ fontSize: "13px" }}
                        className="m-l-5 text-black text-black-2 whitespace-nowrap"
                      >
                        {letter?.for_page?.name}
                      </span>
                    </span>
                  </td>
                  <td />
                  <td
                    // className="text-start"
                    style={{ fontSize: "13px" }}
                    onClick={() =>
                      handleEditModalShow(
                        letter?.for_template?.template?.id,
                        userId,
                        letter?.id,
                        letter?.for_template.template_name,
                        letter?.edit,
                        letter?.edit_by,
                        letter?.edit_by_profile_pic
                      )
                    }
                  >
                    {letter?.for_dropdown?.name}
                  </td>
                  <td />

                  <td style={{ fontSize: "13px" }}>
                    {letter?.last_edited_by && (
                      <div className="d-flex align-items-center p-r-5">
                        <UserChatBubble
                          first_name={letter?.last_edited_by?.first_name || ""}
                          last_name={letter?.last_edited_by?.last_name || ""}
                          profilePic=""
                          onClick={(e) => openChatModel(e, letter)}
                          isActive={
                            userOnlineStatus?.[letter?.last_edited_by?.id]?.isOnline ?? false
                          }
                          width={19}
                          is_active={letter?.last_edited_by?.is_active}
                        />
                      </div>
                    )}
                  </td>
                  <td
                    style={{ fontSize: "13px" }}
                    onClick={() =>
                      handleEditModalShow(
                        letter?.for_template?.template?.id,
                        userId,
                        letter?.id,
                        letter?.for_template.template_name,
                        letter?.edit,
                        letter?.edit_by,
                        letter?.edit_by_profile_pic
                      )
                    }
                  >
                    {letter?.last_edited_at ? formatDate(letter.last_edited_at) : ""}
                  </td>
                  <td />
                  <td>
                    <div className="d-flex justify-content-center space-x-5">
                      <button
                        className="height-20 btn d-flex align-items-center btn-success-hover-green-templates justify-content-center"
                        style={{
                          background: "var(--primary-25)",
                          color: "var(--primary)",
                        }}
                        onClick={() =>
                          handleEditButton(
                            letter?.for_template?.template?.id,
                            userId,
                            letter?.id,
                            letter?.for_template.template_name,
                            letter?.edit,
                            letter?.edit_by,
                            letter?.edit_by_profile_pic
                          )
                        }
                      >
                        <span>Edit</span>
                        <span className="">
                          <i
                            style={{
                              position: "relative",
                              top: "1px",
                            }}
                            className="ic ic-15 edit-template-icon ms-1 "
                          />
                        </span>
                      </button>
                    </div>
                  </td>
                  <td />
                </tr>
              );
            })}
        </tbody>
      </TableFirmSettings>
      {showEditModal && (
        <EditCopyLetterTemplateModal
          show={showEditModal}
          handleClose={() => setShowEditModal(false)}
          size={"lg"}
          title={"Document Template Controls"}
          data={editData}
          refetch={refetch}
        />
      )}

      {chatModal && (
        <ChatSendingPopup
          data={chatData}
          show={chatModal}
          handleClose={() => setChatModal(false)}
          first_name={chatData?.last_edited_by?.first_name}
          last_name={chatData?.last_edited_by?.last_name}
          userId={chatData?.last_edited_by?.id}
          profilePic={avatarImage}
          is_active={chatData?.last_edited_by?.is_active}
        />
      )}

      {
        <Modal show={state.show} onHide={() => dispatch({ type: "HIDE_MODAL" })}>
          <Modal.Header>
            <Modal.Title
              dangerouslySetInnerHTML={{ __html: state.status }}
              className="modal-title h4 justify-content-center text-center"
            />
          </Modal.Header>
          <Modal.Footer style={{ borderTop: "none" }}>
            <Button
              variant="secondary"
              className="mx-auto"
              onClick={() => dispatch({ type: "HIDE_MODAL" })}
            >
              Dismiss
            </Button>
          </Modal.Footer>
        </Modal>
      }
    </>
  );
};

export default LetterTemplatesALL;
